// frontend/src/portal/components/CustomSignIn.jsx
import React, { useState } from "react";
import { useSignIn } from "@clerk/clerk-react";

import { Users, ShieldCheck, FileText, Activity } from "lucide-react";

export default function CustomSignIn({ returnUrl = "/portal" }) {
  const { signIn, isLoaded, setActive } = useSignIn();

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [secondFactor, setSecondFactor] = useState(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getErrorMessage = (err, fallback) =>
    err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || fallback;

  const completeSignIn = async (result) => {
    console.info("[CustomSignIn] Clerk sign-in status transition:", result.status);
    await setActive({ session: result.createdSessionId });
    window.location.href = returnUrl;
  };

  const prepareEmailSecondFactor = async (result) => {
    const emailFactor = result.supportedSecondFactors?.find((factor) => factor.strategy === "email_code");

    console.info(
      "[CustomSignIn] Clerk requires a second factor. Supported strategies:",
      result.supportedSecondFactors?.map((factor) => factor.strategy) || [],
    );

    if (!emailFactor?.emailAddressId) {
      throw new Error("Email verification is not available for this sign-in. Please contact support.");
    }

    // prepareSecondFactor and attemptSecondFactor operate on this same signIn attempt.
    const prepared = await signIn.prepareSecondFactor({
      strategy: "email_code",
      emailAddressId: emailFactor.emailAddressId,
    });
    console.info("[CustomSignIn] Clerk second factor prepared; status:", prepared.status);
    setSecondFactor(emailFactor);
    setVerificationCode("");
  };

  // Handle email/password sign-in
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Create sign-in attempt with identifier (email)
      const result = await signIn.create({
        identifier: email,
        password: password,
      });

      console.info("[CustomSignIn] Clerk password sign-in status:", result.status);

      // Step 2: Complete sign-in or continue Clerk's MFA flow.
      if (result.status === "complete") {
        await completeSignIn(result);
      } else if (result.status === "needs_second_factor") {
        await prepareEmailSecondFactor(result);
      } else {
        console.warn("[CustomSignIn] Unexpected Clerk password sign-in status:", result.status);
        setError("Sign-in incomplete. Please try again.");
      }
    } catch (err) {
      console.error("[CustomSignIn] Sign-in error:", err);

      // Parse Clerk error messages
      const message = getErrorMessage(err, "Sign-in failed. Please check your credentials.");
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecondFactorSubmit = async (e) => {
    e.preventDefault();
    if (!isLoaded || !signIn || !secondFactor) return;

    setIsLoading(true);
    setError(null);
    try {
      const result = await signIn.attemptSecondFactor({ strategy: "email_code", code: verificationCode });
      console.info("[CustomSignIn] Clerk email second-factor status:", result.status);
      if (result.status === "complete") {
        await completeSignIn(result);
      } else {
        setError("Verification is incomplete. Please try again.");
      }
    } catch (err) {
      console.error("[CustomSignIn] Second-factor verification error:", err);
      setError(getErrorMessage(err, "The verification code is invalid or expired. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!isLoaded || !signIn || !secondFactor) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await signIn.prepareSecondFactor({
        strategy: "email_code",
        emailAddressId: secondFactor.emailAddressId,
      });
      console.info("[CustomSignIn] Clerk email second factor resent; status:", result.status);
    } catch (err) {
      console.error("[CustomSignIn] Second-factor resend error:", err);
      setError(getErrorMessage(err, "Unable to resend the verification code. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setError(null);
    setVerificationCode("");
    setSecondFactor(null);
    // This Clerk SDK has no SignIn.reset(). The next password submit calls
    // signIn.create(), which starts a fresh attempt instead of reusing this MFA attempt.
    console.info("[CustomSignIn] Returned to password sign-in; the next submit will create a fresh attempt.");
  };

  // Handle Google OAuth sign-in
  const handleGoogleSignIn = async () => {
    if (!isLoaded || !signIn) return;

    setIsLoading(true);
    setError(null);

    try {
      // Use the current origin for redirect URLs
      const redirectUrl = `${window.location.origin}/portal/sso-callback`;
      const redirectUrlComplete = `${window.location.origin}${returnUrl}`;

      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: redirectUrl,
        redirectUrlComplete: redirectUrlComplete,
      });
      // User will be redirected to Google
    } catch (err) {
      console.error("[CustomSignIn] Google sign-in error:", err);
      setError(err?.errors?.[0]?.message || err?.message || "Google sign-in failed.");
      setIsLoading(false);
    }
  };

  // Don't render until Clerk is loaded
  if (!isLoaded) {
    return (
      <div className="loading-container">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .loading-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          font-family: "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .loading-container p {
          color: #64748b;
        }

        .signin-wrapper {
          min-height: 100vh;
          display: flex;
          font-family: "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif;
          background: #ffffff;
        }

        /* Left side - Form */
        .signin-form-side {
          flex: 1;
          max-width: 100%;
          padding: 32px 24px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          background: #ffffff;
          position: relative;
          z-index: 2;
        }

        .form-container {
          max-width: 440px;
          margin: 0 auto;
          width: 100%;
        }

        .logo-section {
          margin-bottom: 32px;
          animation: slideInLeft 0.6s ease-out;
        }

        .logo-text {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .header-section {
          margin-bottom: 32px;
          animation: slideInLeft 0.6s ease-out 0.1s both;
        }

        .header-title {
          font-size: 28px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 8px 0;
          letter-spacing: -0.03em;
        }

        .header-subtitle {
          font-size: 15px;
          color: #64748b;
          margin: 0;
          font-weight: 400;
        }

        .error-message {
          margin-bottom: 20px;
          padding: 12px 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-size: 14px;
          animation: slideInLeft 0.3s ease-out;
        }

        .signin-form {
          animation: slideInLeft 0.6s ease-out 0.2s both;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          background: #ffffff;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          font-size: 15px;
          color: #0f172a;
          outline: none;
          transition: all 0.2s ease;
          box-sizing: border-box;
          font-family: inherit;
        }

        .form-input:focus {
          border-color: #0ea5e9;
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }

        .form-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .password-wrapper {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          padding: 4px 8px;
          width: 52px;
          min-width: 52px;
          height: 30px;
          box-shadow: none;
          appearance: none;
          transform-origin: center;
        }

        .password-wrapper .form-input {
          padding-right: 76px;
        }

        .password-toggle:hover:not(:disabled),
        .password-toggle:focus-visible {
          background: transparent;
          color: #475569;
          transform: translateY(-50%);
          box-shadow: none;
        }

        .form-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
        }

        .remember-me {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .checkbox {
          width: 16px;
          height: 16px;
          border: 1.5px solid #cbd5e1;
          border-radius: 4px;
          cursor: pointer;
        }

        .checkbox:checked {
          background: #0ea5e9;
          border-color: #0ea5e9;
        }

        .checkbox-label {
          font-size: 14px;
          color: #475569;
          cursor: pointer;
        }

        .forgot-link {
          font-size: 14px;
          color: #0ea5e9;
          text-decoration: none;
          font-weight: 500;
        }

        .signin-button {
          width: 100%;
          padding: 14px;
          background: #0ea5e9;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 16px;
        }

        .signin-button:hover:not(:disabled) {
          background: #0284c7;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
        }

        .signin-button:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }

        .divider {
          display: flex;
          align-items: center;
          margin: 24px 0;
          color: #94a3b8;
          font-size: 13px;
        }

        .divider::before,
        .divider::after {
          content: "";
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        .divider::before {
          margin-right: 12px;
        }

        .divider::after {
          margin-left: 12px;
        }

        .google-button {
          width: 100%;
          padding: 12px 16px;
          background: #ffffff;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          color: #334155;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .google-button:hover:not(:disabled) {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .google-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .footer-section {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #e2e8f0;
          animation: slideInLeft 0.6s ease-out 0.3s both;
        }

        .footer-link {
          font-size: 14px;
          color: #0ea5e9;
          text-decoration: none;
          font-weight: 500;
          display: block;
          margin-bottom: 8px;
        }

        .footer-text {
          font-size: 12px;
          color: #94a3b8;
          margin: 0;
          line-height: 1.6;
          display: flex;
          gap: 12px;
        }

        .footer-text a {
          color: #0ea5e9;
          text-decoration: none;
        }

        /* Right side - Hero */
        .signin-hero-side {
          flex: 1;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 40px;
          position: relative;
          overflow: hidden;
        }

        .hero-grid {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: fadeIn 1s ease-out;
        }

        .hero-orb-1 {
          position: absolute;
          top: 20%;
          left: 15%;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(14, 165, 233, 0.3) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
          animation: float 8s ease-in-out infinite;
        }

        .hero-orb-2 {
          position: absolute;
          bottom: 15%;
          right: 20%;
          width: 250px;
          height: 250px;
          background: radial-gradient(circle, rgba(56, 189, 248, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(50px);
          animation: float 6s ease-in-out infinite reverse;
        }

        .hero-content {
          position: relative;
          z-index: 1;
          text-align: center;
          animation: fadeIn 1s ease-out 0.3s both;
          max-width: 600px;
        }

        .hero-title {
          font-size: 42px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 16px 0;
          letter-spacing: -0.03em;
          line-height: 1.2;
        }

        .hero-gradient-text {
          background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 16px;
          color: #94a3b8;
          margin: 0 auto 32px;
          max-width: 480px;
          line-height: 1.6;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-top: 48px;
        }

        .feature-card {
          background: rgba(14, 165, 233, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(14, 165, 233, 0.1);
          border-radius: 12px;
          padding: 20px 16px;
          text-align: left;
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          background: rgba(14, 165, 233, 0.1);
          border-color: rgba(14, 165, 233, 0.2);
          transform: translateY(-4px);
        }

        .feature-icon {
          margin-bottom: 12px;
          color: #38bdf8;
        }

        .feature-title {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          margin: 0 0 6px 0;
          letter-spacing: -0.01em;
        }

        .feature-desc {
          font-size: 12px;
          color: #94a3b8;
          margin: 0;
          line-height: 1.5;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .signin-wrapper {
            flex-direction: column;
          }

          .signin-hero-side {
            display: none;
          }

          .signin-form-side {
            padding: 24px 20px;
            min-height: 100vh;
          }

          .logo-text {
            font-size: 22px;
          }

          .header-title {
            font-size: 24px;
          }

          .header-subtitle {
            font-size: 14px;
          }

          .logo-section {
            margin-bottom: 24px;
          }

          .header-section {
            margin-bottom: 24px;
          }

          .form-input {
            font-size: 16px; /* Prevents zoom on iOS */
          }
        }

        /* Tablet */
        @media (min-width: 769px) and (max-width: 1024px) {
          .signin-form-side {
            flex: 0 0 400px;
            padding: 40px 32px;
          }

          .hero-title {
            font-size: 36px;
          }

          .hero-subtitle {
            font-size: 15px;
          }

          .features-grid {
            gap: 12px;
          }

          .feature-card {
            padding: 16px 14px;
          }

          .signin-hero-side {
            padding: 40px 32px;
          }
        }

        /* Desktop */
        @media (min-width: 1025px) {
          .signin-form-side {
            flex: 0 0 480px;
            padding: 60px 80px;
          }
        }

        /* Small mobile */
        @media (max-width: 375px) {
          .signin-form-side {
            padding: 20px 16px;
          }

          .header-title {
            font-size: 22px;
          }

          .signin-button,
          .google-button {
            padding: 12px;
            font-size: 14px;
          }
        }
      `}</style>

      <div className="signin-wrapper">
        {/* Left Side - Form */}
        <div className="signin-form-side">
          <div className="form-container">
            {/* Logo */}
            <div className="logo-section">
              <div className="logo-text">CBCFX</div>
            </div>

            {/* Header */}
            <div className="header-section">
              <h1 className="header-title">{secondFactor ? "Check your email" : "Welcome back"}</h1>
              <p className="header-subtitle">
                {secondFactor ? "Enter the verification code Clerk sent to your email." : "Sign in to your organization"}
              </p>
            </div>

            {/* Error Message */}
            {error && <div className="error-message">{error}</div>}

            {/* Form */}
            {secondFactor ? (
              <form onSubmit={handleSecondFactorSubmit} className="signin-form">
                <div className="form-group">
                  <label className="form-label" htmlFor="second-factor-code">
                    Verification code
                  </label>
                  <input
                    id="second-factor-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter verification code"
                    disabled={isLoading}
                    required
                    autoFocus
                    className="form-input"
                  />
                </div>
                <button type="submit" disabled={isLoading || !verificationCode.trim()} className="signin-button">
                  {isLoading ? "Verifying..." : "Verify and sign in"}
                </button>
                <button type="button" onClick={handleResendCode} disabled={isLoading} className="google-button">
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  disabled={isLoading}
                  className="forgot-link"
                  style={{ background: "none", border: 0, padding: "16px 0 0", cursor: "pointer" }}
                >
                  ← Back to sign in
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="signin-form">
              {/* Email */}
              <div className="form-group">
                <label className="form-label">Work email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" disabled={isLoading} required className="form-input" autoComplete="email" />
              </div>

              {/* Password */}
              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    disabled={isLoading}
                    required
                    className="form-input"
                    autoComplete="current-password"
                  />
                  <button type="button" onClick={() => setShowPassword((s) => !s)} disabled={isLoading} className="password-toggle">
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {/* Remember me & Forgot password */}
              <div className="form-row">
                <label className="remember-me">
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} disabled={isLoading} className="checkbox" />
                  <span className="checkbox-label">Remember me</span>
                </label>
                <a href="/portal/forgot-password" className="forgot-link">
                  Forgot password?
                </a>
              </div>

              {/* Sign in button */}
              <button type="submit" disabled={isLoading} className="signin-button">
                {isLoading ? "Signing in..." : "Sign in"}
              </button>

              {/* Divider */}
              <div className="divider">or</div>

              {/* Google Sign-in */}
              <button type="button" onClick={handleGoogleSignIn} disabled={isLoading} className="google-button">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.909-2.258c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>
              </form>
            )}

            {/* Footer */}
            <div className="footer-section">
              <a href="/portal/request-access" className="footer-link">
                Need access? Submit an access request →
              </a>
              <p className="footer-text">
                <span>© CBCFX</span>
                <a href="/portal/privacy">Privacy</a>
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Hero */}
        <div className="signin-hero-side">
          {/* Animated grid background */}
          <div className="hero-grid" />

          {/* Glowing orbs */}
          <div className="hero-orb-1" />
          <div className="hero-orb-2" />

          <div className="hero-content">
            <h2 className="hero-title">
              Compliance Workspace
              <br />
              <span className="hero-gradient-text">Simplified.</span>
            </h2>
            <p className="hero-subtitle">Access your tenant workspace to manage clients, documents, risk reviews, and approvals.</p>

            {/* Feature highlights */}
            <div className="features-grid">
              {[
                { icon: Users, title: "Tenant Workspaces", desc: "Separate client data per firm/tenant." },
                { icon: ShieldCheck, title: "KYC & Risk Reviews", desc: "Structured reviews with due dates." },
                { icon: FileText, title: "Documents & Expiry Alerts", desc: "Upload, validate, track expirations." },
                { icon: Activity, title: "Audit Trail", desc: "Immutable log of key actions." },
              ].map((feature, i) => (
                <div key={i} className="feature-card">
                  <div className="feature-icon">
                    <feature.icon size={24} strokeWidth={1.8} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-desc">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
